import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Popconfirm, Space, Input, Select, Modal, Form, Typography, Tabs, Card, Badge, message, Dropdown, Descriptions, Tooltip, Row, Col } from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, EditOutlined,
  AppstoreOutlined, EyeOutlined, CloudUploadOutlined,
  BranchesOutlined, QrcodeOutlined, SettingOutlined,
  MoreOutlined, SearchOutlined, ReloadOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const { Title, Text } = Typography;

const statusMap = {
  developing: { label: '开发中', color: 'blue' },
  auditing: { label: '审核中', color: 'orange' },
  approved: { label: '已通过', color: 'cyan' },
  rejected: { label: '已拒绝', color: 'red' },
  published: { label: '已发布', color: 'green' },
  disabled: { label: '已禁用', color: 'default' },
};

const versionTypeMap = {
  develop: { label: '开发版', color: 'blue' },
  trial: { label: '体验版', color: 'orange' },
  release: { label: '正式版', color: 'green' },
};

function MiniProgramPage() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, developing: 0, auditing: 0 });
  const [form] = Form.useForm();
  const [addForm] = Form.useForm();

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 50 };
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category_id = filterCategory;
      if (searchKeyword) params.keyword = searchKeyword;
      const { list } = await api.get('/admin/mini-programs', { params });
      setPrograms(list || []);
      // 计算统计
      setStats({
        total: list?.length || 0,
        published: list?.filter(p => p.status === 'published').length || 0,
        developing: list?.filter(p => p.status === 'developing').length || 0,
        auditing: list?.filter(p => p.status === 'auditing').length || 0,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const { list } = await api.get('/admin/mini-programs/categories', { params: { limit: 100 } });
      setCategories(list || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadPrograms();
  }, [filterStatus, filterCategory]);

  useEffect(() => {
    loadCategories();
  }, []);

  const handleApprove = async (id) => {
    await api.put(`/admin/mini-programs/${id}`, { status: 'published' });
    message.success('审核通过');
    loadPrograms();
  };

  const handleReject = async (id, reason) => {
    await api.put(`/admin/mini-programs/${id}`, { status: 'rejected', rejected_reason: reason || '不符合规范' });
    message.success('已拒绝');
    loadPrograms();
  };

  const handleQuickPublish = async (id) => {
    try {
      await api.post(`/admin/mini-programs/${id}/publish`);
      message.success('快速发布成功');
      loadPrograms();
    } catch (e) {
      message.error('发布失败');
    }
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ name: record.name, description: record.description });
    setEditModal(true);
  };

  const handleEditSave = async () => {
    const values = form.getFieldsValue();
    await api.put(`/admin/mini-programs/${editing.id}`, values);
    message.success('保存成功');
    setEditModal(false);
    loadPrograms();
  };

  const handleAdd = () => {
    addForm.resetFields();
    setAddModal(true);
  };

  const handleAddSave = async () => {
    try {
      const values = await addForm.validateFields();
      await api.post('/admin/mini-programs', values);
      message.success('小程序创建成功');
      setAddModal(false);
      loadPrograms();
    } catch (e) {
      message.error(e.errorFields ? '请填写完整' : '创建失败');
    }
  };

  const handleSearch = () => {
    loadPrograms();
  };

  const filteredPrograms = programs.filter(p => {
    if (activeTab === 'all') return true;
    if (activeTab === 'published') return p.status === 'published';
    if (activeTab === 'developing') return p.status === 'developing';
    if (activeTab === 'auditing') return p.status === 'auditing';
    if (activeTab === 'rejected') return p.status === 'rejected';
    return true;
  });

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '小程序名称', width: 180,
      render: (_, r) => (
        <Space>
          <AppstoreOutlined style={{ color: '#07C160' }} />
          <Text strong>{r.name}</Text>
        </Space>
      ),
    },
    {
      title: 'AppID', width: 200,
      render: (_, r) => <Tag color="geekblue" style={{ fontFamily: 'monospace' }}>{r.app_id || '-'}</Tag>,
    },
    {
      title: '分类', width: 100,
      render: (_, r) => r.category_name ? <Tag color="purple">{r.category_name}</Tag> : '-',
    },
    {
      title: '当前版本', width: 110,
      render: (_, r) => r.current_version ? (
        <Space>
          <BranchesOutlined style={{ color: '#1890ff' }} />
          <Text>{r.current_version}</Text>
        </Space>
      ) : '-',
    },
    {
      title: '状态', width: 100,
      render: (_, r) => (
        <Badge status={statusMap[r.status]?.color === 'green' ? 'success' : statusMap[r.status]?.color === 'red' ? 'error' : statusMap[r.status]?.color === 'orange' ? 'warning' : 'processing'} />
      ),
    },
    {
      title: '状态标签', width: 100,
      render: (_, r) => <Tag color={statusMap[r.status]?.color}>{statusMap[r.status]?.label || r.status}</Tag>,
    },
    {
      title: '创建者', width: 100,
      render: (_, r) => r.creator || '-',
    },
    {
      title: '创建时间', width: 160,
      render: (_, r) => r.created_at || '-',
    },
    {
      title: '操作', width: 220,
      fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/mini-programs/${r.id}`)}
          >
            详情
          </Button>
          {(r.status === 'approved' || r.has_approved_version) && (
            <Button
              size="small"
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={() => handleQuickPublish(r.id)}
              style={{ background: '#07C160' }}
            >
              快速发布
            </Button>
          )}
          <Dropdown
            menu={{
              items: [
                { key: 'edit', icon: <EditOutlined />, label: '编辑', onClick: () => handleEdit(r) },
                ...(r.status === 'pending' || r.status === 'auditing') ? [
                  { key: 'approve', icon: <CheckCircleOutlined />, label: '审核通过', onClick: () => handleApprove(r.id) },
                  { key: 'reject', icon: <CloseCircleOutlined />, label: '审核拒绝', danger: true, onClick: () => handleReject(r.id) },
                ] : [],
              ],
            }}
          >
            <Button size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'all',
      label: <span><AppstoreOutlined /> 全部 <Badge count={programs.length} style={{ backgroundColor: '#52c41a' }} /></span>,
      children: (
        <Table
          columns={columns}
          dataSource={filteredPrograms}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showTotal: (total) => `共 ${total} 条` }}
          scroll={{ x: 1400 }}
        />
      ),
    },
    {
      key: 'published',
      label: <span><CheckCircleOutlined style={{ color: '#52c41a' }} /> 已发布 <Badge count={stats.published} style={{ backgroundColor: '#52c41a' }} /></span>,
      children: (
        <Table
          columns={columns}
          dataSource={filteredPrograms}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showTotal: (total) => `共 ${total} 条` }}
          scroll={{ x: 1400 }}
        />
      ),
    },
    {
      key: 'developing',
      label: <span><BranchesOutlined style={{ color: '#1890ff' }} /> 开发中 <Badge count={stats.developing} style={{ backgroundColor: '#1890ff' }} /></span>,
      children: (
        <Table
          columns={columns}
          dataSource={filteredPrograms}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showTotal: (total) => `共 ${total} 条` }}
          scroll={{ x: 1400 }}
        />
      ),
    },
    {
      key: 'auditing',
      label: <span><QrcodeOutlined style={{ color: '#fa8c16' }} /> 审核中 <Badge count={stats.auditing} style={{ backgroundColor: '#fa8c16' }} /></span>,
      children: (
        <Table
          columns={columns}
          dataSource={filteredPrograms}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showTotal: (total) => `共 ${total} 条` }}
          scroll={{ x: 1400 }}
        />
      ),
    },
  ];

  return (
    <>
      {/* 顶部统计卡片 */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="小程序总数">
                <Text strong style={{ fontSize: 20, color: '#52c41a' }}>{stats.total}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="已发布">
                <Text strong style={{ fontSize: 20, color: '#52c41a' }}>{stats.published}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card size="small" style={{ background: '#e6f7ff', border: '1px solid #91d5ff' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="开发中">
                <Text strong style={{ fontSize: 20, color: '#1890ff' }}>{stats.developing}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card size="small" style={{ background: '#fff7e6', border: '1px solid #ffd591' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="审核中">
                <Text strong style={{ fontSize: 20, color: '#fa8c16' }}>{stats.auditing}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </div>
      </div>

      {/* 顶部操作栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>小程序管理</Title>
        <Space>
          <Button
            icon={<PlusOutlined />}
            onClick={handleAdd}
            style={{ background: '#07C160', color: '#fff', borderColor: '#07C160' }}
          >
            新建小程序
          </Button>
          <Button icon={<SettingOutlined />} onClick={() => navigate('/mini-program-categories')}>分类管理</Button>
          <Button icon={<ReloadOutlined />} onClick={loadPrograms}>刷新</Button>
        </Space>
      </div>

      {/* 筛选栏 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="搜索小程序名称或AppID"
            prefix={<SearchOutlined />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 220 }}
            allowClear
          />
          <Select
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: 120 }}
            placeholder="状态筛选"
            options={[
              { value: '', label: '全部状态' },
              { value: 'developing', label: '开发中' },
              { value: 'auditing', label: '审核中' },
              { value: 'approved', label: '已通过' },
              { value: 'rejected', label: '已拒绝' },
              { value: 'published', label: '已发布' },
            ]}
          />
          <Select
            value={filterCategory}
            onChange={setFilterCategory}
            style={{ width: 140 }}
            placeholder="分类筛选"
            options={[
              { value: '', label: '全部分类' },
              ...categories.map(c => ({ value: c.id, label: c.name })),
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
        </Space>
      </Card>

      {/* 主内容 - Tabs */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="card"
        />
      </Card>

      {/* 编辑弹窗 */}
      <Modal
        title="编辑小程序"
        open={editModal}
        onOk={handleEditSave}
        onCancel={() => setEditModal(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称"><Input /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea /></Form.Item>
        </Form>
      </Modal>

      {/* 新建小程序弹窗 */}
      <Modal
        title="新建小程序"
        open={addModal}
        onOk={handleAddSave}
        onCancel={() => { setAddModal(false); addForm.resetFields(); }}
        width={600}
      >
        <Form form={addForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="小程序名称" rules={[{ required: true, message: '请输入名称' }]}>
                <Input placeholder="请输入小程序名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="categoryId" label="分类">
                <Select placeholder="选择分类" allowClear>
                  {categories.map(c => (
                    <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="appId" label="AppID">
                <Input placeholder="选填" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="appSecret" label="AppSecret">
                <Input placeholder="选填" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} placeholder="小程序描述" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default MiniProgramPage;
